<template>
  <div class="flex justify-center items-center px-6 lg:px-10 py-16 lg:py-20">
    <div class="max-w-3xl w-full">
      <a
        href="/blog"
        class="flex items-center space-x-2 text-woven-light-green hover:text-woven-green transition-colors duration-200 mb-8 group"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4 transform group-hover:-translate-x-1 transition-transform duration-200"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
        <span class="uppercase tracking-widest font-semibold text-sm"
          >Back to Insights</span
        >
      </a>
      <div class="flex items-center space-x-2 mb-6">
        <span
          v-for="category in post.categories"
          :key="category.id"
          class="px-3 py-2 bg-woven-green text-black rounded-full text-sm"
        >
          {{ post._embedded["wp:term"][0][0].name }}
        </span>
      </div>
      <h1
        class="text-4xl lg:text-5xl font-medium text-white mb-4"
        v-html="post.title.rendered"
      ></h1>
      <div class="flex flex-wrap items-center text-base text-slate-400 mb-8">
        <p class="italic mr-4">
          {{ formattedDate }}
          <span v-if="authorInfo"> &mdash; {{ authorInfo }}</span>
        </p>
        <div class="flex items-center mt-2 sm:mt-0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-1 text-woven-light-green"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{{ readingTime }} min read</span>
        </div>
      </div>
      <div
        v-if="post.jetpack_featured_media_url"
        class="mb-8 rounded-lg overflow-hidden shadow-lg"
      >
        <img
          :src="post.jetpack_featured_media_url"
          alt="Featured Image"
          class="w-full h-auto"
        />
      </div>
      <div class="bg-slate-900/30 rounded-lg p-4 lg:p-8 shadow-lg mb-8">
        <iframe
          ref="contentIframe"
          class="w-full blog-iframe"
          :style="{ height: iframeHeight + 'px' }"
          :srcdoc="iframeContent"
          scrolling="no"
          @load="onIframeLoad"
        ></iframe>
      </div>

      <!-- Social Sharing -->
      <div class="border-t border-slate-700 pt-6">
        <h3 class="text-xl text-woven-light-green mb-4">Share This Post</h3>
        <div class="flex flex-wrap gap-4">
          <!-- X (formerly Twitter) -->
          <button
            class="w-10 h-10 flex items-center justify-center bg-slate-800 hover:bg-slate-700 rounded-full transition-colors duration-200"
            title="Share on X"
            @click="shareToTwitter"
          >
            <svg
              class="w-5 h-5 text-white"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <!-- X logo SVG path -->
              <path
                d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
              ></path>
            </svg>
          </button>

          <!-- LinkedIn -->
          <button
            class="w-10 h-10 flex items-center justify-center bg-slate-800 hover:bg-slate-700 rounded-full transition-colors duration-200"
            title="Share on LinkedIn"
            @click="shareToLinkedIn"
          >
            <svg
              class="w-5 h-5 text-[#0A66C2]"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.454C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"
              ></path>
            </svg>
          </button>

          <!-- Facebook -->
          <button
            class="w-10 h-10 flex items-center justify-center bg-slate-800 hover:bg-slate-700 rounded-full transition-colors duration-200"
            title="Share on Facebook"
            @click="shareToFacebook"
          >
            <svg
              class="w-5 h-5 text-[#4267B2]"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                clip-rule="evenodd"
              ></path>
            </svg>
          </button>

          <!-- WhatsApp -->
          <button
            class="w-10 h-10 flex items-center justify-center bg-slate-800 hover:bg-slate-700 rounded-full transition-colors duration-200"
            title="Share on WhatsApp"
            @click="shareToWhatsapp"
          >
            <svg
              class="w-5 h-5 text-[#25D366]"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M17.498 14.382c-.301-.15-1.767-.867-2.04-.966-.273-.101-.473-.15-.673.15-.197.295-.771.964-.944 1.162-.175.195-.349.21-.646.075-.3-.15-1.263-.465-2.403-1.485-.888-.795-1.484-1.77-1.66-2.07-.174-.3-.019-.465.13-.615.136-.135.301-.345.451-.523.146-.181.194-.301.297-.496.1-.21.049-.375-.025-.524-.075-.15-.672-1.62-.922-2.206-.24-.584-.487-.51-.672-.51-.172-.015-.371-.015-.571-.015-.2 0-.523.074-.797.359-.273.3-1.045 1.02-1.045 2.475s1.07 2.865 1.219 3.075c.149.195 2.105 3.195 5.1 4.485.714.3 1.27.48 1.704.629.714.227 1.365.195 1.88.121.574-.091 1.767-.721 2.016-1.426.255-.705.255-1.29.18-1.425-.074-.135-.27-.21-.57-.345m-5.446 7.443h-.016c-1.77 0-3.524-.48-5.055-1.38l-.36-.214-3.75.975 1.005-3.645-.239-.375c-.99-1.576-1.516-3.391-1.516-5.26 0-5.445 4.455-9.885 9.942-9.885 2.654 0 5.145 1.035 7.021 2.91 1.875 1.859 2.909 4.35 2.909 6.99-.004 5.444-4.46 9.885-9.935 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.893c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652c1.746.943 3.71 1.444 5.71 1.447h.006c6.585 0 11.946-5.336 11.949-11.896 0-3.176-1.24-6.165-3.495-8.411"
              ></path>
            </svg>
          </button>

          <!-- Email -->
          <button
            class="w-10 h-10 flex items-center justify-center bg-slate-800 hover:bg-slate-700 rounded-full transition-colors duration-200"
            title="Share via Email"
            @click="shareViaEmail"
          >
            <svg
              class="w-5 h-5 text-gray-300"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"
              ></path>
              <path
                d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"
              ></path>
            </svg>
          </button>

          <!-- Copy Link Button -->
          <button
            class="w-10 h-10 flex items-center justify-center bg-slate-800 hover:bg-slate-700 rounded-full transition-colors duration-200 relative"
            title="Copy link to clipboard"
            @click="copyToClipboard"
          >
            <svg
              class="w-5 h-5 text-slate-300"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z"
                clip-rule="evenodd"
              ></path>
            </svg>
            <span
              v-if="linkCopied"
              class="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-slate-700 text-white text-xs rounded"
            >
              Copied!
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";

export default {
  props: {
    post: Object,
  },
  data() {
    return {
      iframeHeight: 0,
      iframeContent: "",
      linkCopied: false,
    };
  },
  computed: {
    formattedDate() {
      return dayjs(this.post.date).format("MMMM D, YYYY");
    },
    authorInfo() {
      const { attributable_author_name, attributable_author_job_title } =
        this.post.acf;

      if (attributable_author_name) {
        if (attributable_author_job_title) {
          return `${attributable_author_name}, ${attributable_author_job_title}`;
        }
        return `${attributable_author_name}`;
      }
      return null;
    },
    readingTime() {
      // Calculate reading time based on content length
      // Average reading speed: 225 words per minute
      const wordCount = this.post.content.rendered
        .replace(/<[^>]*>/g, "") // Remove HTML tags
        .split(/\s+/)
        .filter((word) => word.length > 0).length;

      const minutes = Math.ceil(wordCount / 225);
      return minutes > 0 ? minutes : 1; // Minimum 1 minute
    },
    currentUrl() {
      return window.location.href;
    },
    postTitle() {
      // Get plain text title by removing HTML tags
      return this.post.title.rendered.replace(/<[^>]*>/g, "");
    },
  },
  mounted() {
    // Prepare the iframe content
    this.iframeContent = `
    <html>
      <head>
        <base target="_parent">
        <style>
          html{line-height:1.15;-webkit-text-size-adjust:100%}*,:after,:before{box-sizing:border-box}body{margin:0;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica Neue,Arial,Noto Sans,sans-serif,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol,Noto Color Emoji;font-size:1rem;font-weight:400;line-height:1.5;color:#e6e6e6;background-color:transparent;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale}h1,h2,h3,h4,h5,h6{margin-block-start:.5rem;margin-block-end:1rem;font-family:inherit;font-weight:500;line-height:1.2;color:inherit}h1{font-size:2.5rem}h2{font-size:2rem;color:#cff1de;margin-top:2rem}h3{font-size:1.75rem;color:#b4e3c9;margin-top:1.75rem}h4{font-size:1.5rem;color:#cff1de}h5{font-size:1.25rem}h6{font-size:1rem}p{margin-block-start:0;margin-block-end:1.25rem;line-height:1.7;font-size:1.05rem;letter-spacing:0.01em}hr{box-sizing:content-box;height:0;overflow:visible;border:0;border-top:1px solid rgba(207, 241, 222, 0.2);margin:2rem 0}pre{font-family:monospace,monospace;font-size:1em;white-space:pre-wrap}a{background-color:transparent;text-decoration:none;color:#cff1de;border-bottom:1px dotted rgba(207, 241, 222, 0.4);transition:all 0.2s ease}a:active,a:hover{color:#ffffff;border-bottom:1px solid rgba(207, 241, 222, 0.8)}a:not([href]):not([tabindex]),a:not([href]):not([tabindex]):focus,a:not([href]):not([tabindex]):hover{color:inherit;text-decoration:none}a:not([href]):not([tabindex]):focus{outline:0}abbr[title]{border-block-end:none;-webkit-text-decoration:underline dotted;text-decoration:underline dotted}b,strong{font-weight:bolder;color:#ffffff}code,kbd,samp{font-family:monospace,monospace;font-size:1em}small{font-size:80%}sub,sup{font-size:75%;line-height:0;position:relative;vertical-align:baseline}sub{bottom:-.25em}sup{top:-.5em}img{border-style:none;height:auto;max-width:100%;border-radius:4px;box-shadow:0 4px 8px rgba(0,0,0,0.1)}details{display:block}summary{display:list-item}figcaption{font-size:16px;color:#b0b0b0;line-height:1.4;font-style:italic;font-weight:400;text-align:center;margin-top:0.5rem}[hidden],template{display:none}

          /* Lists styling */
          ul, ol {
            padding-left: 1.5rem;
            margin-bottom: 1.5rem;
          }
          
          li {
            margin-bottom: 0.5rem;
            line-height: 1.6;
          }
          
          /* Blockquote styling */
          blockquote {
            border-left: 4px solid rgba(207, 241, 222, 0.6);
            padding-left: 1.5rem;
            margin-left: 0;
            margin-right: 0;
            font-style: italic;
            color: #b0b0b0;
          }
          
          blockquote p {
            margin-bottom: 0.75rem;
          }
          
          blockquote cite {
            font-style: normal;
            font-size: 0.9rem;
            color: #999;
          }
          
          /* Table styling */
          table {
            width: 100%;
            margin-bottom: 2rem;
            border-collapse: collapse;
          }
          
          table th {
            background-color: rgba(207, 241, 222, 0.1);
            color: #ffffff;
            font-weight: 600;
            text-align: left;
          }
          
          table td, table th {
            padding: 0.75rem;
            border: 1px solid rgba(150, 150, 150, 0.2);
          }
          
          table tr:nth-child(even) {
            background-color: rgba(150, 150, 150, 0.05);
          }

/* Dark theme for code blocks - enhanced */
pre,
code {
  background-color: #1a1a1a;
  color: #e6e6e6;
  font-family: "JetBrains Mono", "Fira Code", "Courier New", Courier, monospace;
  border-radius: 5px;
  font-size: 0.9rem;
}

pre {
  padding: 1.2rem;
  margin: 1.5rem 0;
  overflow-x: auto;
  white-space: pre-wrap;
  border: 1px solid rgba(150, 150, 150, 0.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

code {
  background-color: rgba(30, 30, 30, 0.6);
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
}

pre code {
  background-color: transparent;
  padding: 0;
}

.token.comment,
.token.block-comment,
.token.prolog,
.token.doctype,
.token.cdata {
  color: #999999;
}

.token.punctuation {
  color: #cccccc;
}

.token.property,
.token.tag,
.token.boolean,
.token.number,
.token.constant,
.token.symbol,
.token.deleted {
  color: #ff9d00;
}

.token.selector,
.token.attr-name,
.token.string,
.token.char,
.token.builtin,
.token.inserted {
  color: #a8ff60;
}

.token.operator,
.token.entity,
.token.url,
.token.variable {
  color: #67cdcc;
}

.token.atrule,
.token.attr-value,
.token.function,
.token.class-name {
  color: #ffcc00;
}

.token.keyword {
  color: #cc99cd;
}

.token.regex,
.token.important {
  color: #e90;
}

.wp-block-code {
  background-color: #1a1a1a;
  color: #e6e6e6;
  padding: 1.2rem;
  border-radius: 5px;
  overflow-x: auto;
  white-space: pre-wrap;
  border: 1px solid rgba(150, 150, 150, 0.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.wp-block-code code {
  background-color: transparent;
  color: inherit;
  padding: 0;
}

/* Images and captions */
figure {
  margin: 2rem 0;
}

figure.wp-block-image.aligncenter {
  display: flex;
  flex-direction: column;
  align-items: center;
}

figure img {
  max-width: 100%;
  height: auto;
  display: block;
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.has-text-align-center {
  text-align: center;
}

/* General WordPress content */
.wp-block-embed {
  margin-bottom: 1.5rem;
}

.wp-block-separator {
  border-top: 1px solid rgba(207, 241, 222, 0.2);
  margin: 2rem 0;
}

/* Improved spacing */
.blog-content > *:first-child {
  margin-top: 0;
}

.blog-content > *:last-child {
  margin-bottom: 0;
}

/* Add styles for figures and captions */
figcaption {
  color: #b0b0b0;
  font-size: 0.9rem;
  text-align: center;
  margin-top: 0.5rem;
  font-style: italic;
}

        </style>
      </head>
      <body>
        <div class="blog-content">
          ${this.post.content.rendered}
        </div>
      </body>
    </html>
  `;
  },
  methods: {
    onIframeLoad() {
      this.adjustIframeHeight();
    },
    adjustIframeHeight() {
      const iframe = this.$refs.contentIframe;
      if (iframe) {
        this.iframeHeight = iframe.contentWindow.document.body.scrollHeight;
      }
    },
    copyToClipboard() {
      navigator.clipboard.writeText(this.currentUrl).then(() => {
        this.linkCopied = true;
        setTimeout(() => {
          this.linkCopied = false;
        }, 2000);
      });
    },
    shareToTwitter() {
      const text = encodeURIComponent(this.postTitle);
      const url = encodeURIComponent(this.currentUrl);
      const hashtags = "WovenInsights";
      window.open(
        `https://twitter.com/intent/tweet?url=${url}&text=${text}&hashtags=${hashtags}`,
        "_blank"
      );
    },
    shareToFacebook() {
      const url = encodeURIComponent(this.currentUrl);
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${url}`,
        "_blank"
      );
    },
    shareToLinkedIn() {
      const url = encodeURIComponent(this.currentUrl);
      const title = encodeURIComponent(this.postTitle);
      const summary = encodeURIComponent(
        this.post.excerpt?.rendered?.replace(/<[^>]*>/g, "") || ""
      );
      window.open(
        `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}&summary=${summary}`,
        "_blank"
      );
    },
    shareToWhatsapp() {
      const url = encodeURIComponent(this.currentUrl);
      const text = encodeURIComponent(this.postTitle);
      window.open(`https://wa.me/?text=${text}%20${url}`, "_blank");
    },
    shareViaEmail() {
      const subject = encodeURIComponent(this.postTitle);
      const body = encodeURIComponent(
        `Check out this article: ${this.currentUrl}`
      );
      window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
    },
  },
};
</script>

<style lang="scss"></style>
